/**
 * Onglet « Référents » : qui contribue, où en est chacun, et à quelle distance du
 * groupe se situe sa contribution (§8.2).
 *
 * Séparé de l'onglet « Référentiel », qui répond à une autre question : celui-ci
 * porte les PERSONNES et leur progression, l'autre porte l'OBJET produit
 * (config, consensus, figement). Les mélanger obligeait à chercher un bouton de
 * relance au milieu d'une pyramide.
 */
import { useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { ReferentsInviterCard } from "./ReferentsInviterCard";
import { ReferentsSuiviCard } from "./ReferentsSuiviCard";
import { dateCourte } from "@repo/utils";

type Props = {
  campagneId: Id<"campagnes">;
  /** Référentiel dont on suit les référents — celui du test sélectionné. */
  cible: "pami" | "pc-fit";
  /** Portail admin : consultation seule (ni invitation ni relance). */
  lectureSeule?: boolean;
};

export function ReferentsTab({
  campagneId,
  cible,
  lectureSeule = false,
}: Props) {
  const suivi = useQuery(api.referentiel.suivi.obtenirSuiviReferents, {
    campagneId,
    cible,
  });

  if (suivi === undefined) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (suivi === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Référents</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {cible === "pc-fit"
              ? "Le référentiel PC Fit de cette campagne est en cours de préparation. Rechargez la page si ce message persiste."
              : "Cette campagne n'a pas encore de référentiel configuré : activez-le dans l'onglet « Référentiel » avant de désigner des référents."}
          </p>
        </CardContent>
      </Card>
    );
  }

  const { config, consensus, versionCourante, repereHasard, referents } = suivi;
  const nbRepondants = referents.filter((r) => r.statut === "termine").length;
  const enAttente = referents.filter((r) => r.statut !== "termine").length;
  // Le référentiel PAMI est rempli par UN SEUL référent (le créateur de la
  // campagne ou quelqu'un d'autre) : ni consensus, ni divergence, ni seuil de 3.
  const referentUnique = config.cible === "pami";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-3">
            {referentUnique
              ? "Référent de la campagne"
              : "Référents de la campagne"}
            <Badge variant="info">
              {nbRepondants} contribution{nbRepondants > 1 ? "s" : ""} retenue
              {nbRepondants > 1 ? "s" : ""}
            </Badge>
            {enAttente > 0 && (
              <Badge variant="warning">{enAttente} en attente</Badge>
            )}
            {versionCourante && (
              <Badge variant={versionCourante.fige ? "purple" : "outline"}>
                v{versionCourante.version} ·{" "}
                {versionCourante.fige ? "Figé" : "Ouvert"} ·{" "}
                {dateCourte(versionCourante.computedAt)}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {versionCourante === null && (
            <p className="text-sm text-muted-foreground">
              Aucune contribution validée pour l'instant : le référentiel est
              configuré mais vide, et aucun score de matching n'est calculable.
            </p>
          )}
        </CardContent>
      </Card>

      {!lectureSeule && (
        <ReferentsInviterCard
          campagneId={campagneId}
          cible={config.cible}
          nbReferents={referents.length}
        />
      )}

      <ReferentsSuiviCard
        referents={referents}
        dimensions={config.dimensions}
        consensus={consensus}
        echelle={config.echelle}
        cible={config.cible}
        repereHasard={repereHasard}
        lectureSeule={lectureSeule}
      />
    </div>
  );
}
